import { useCallback, useRef } from "react";

import * as yup from "yup";

import { useTiVoDeviceData } from "components/form/fields/TiVoTargets";
import {
  usePromoteVariant,
  ReleaseStage,
  usePromoteAndPublish,
  useVariantPublications,
} from "hooks/tivoApps";
import {
  TiVoAppProvider,
  TiVoAppVariantVersion,
  TiVoAssets,
} from "hooks/tivoApps/types";
import { IMAGES } from "pages/TiVoApps/constants";

import { getPublicationsOutsideTargets } from "../components";

export type VariantPromotionWarningType =
  | "images"
  | "targets"
  | "publishingConfirmation";

export type VariantPromotionWarning = {
  type: VariantPromotionWarningType;
  message?: string;
};

export interface UseVariantPromotionProps {
  id?: string;
  variantId?: string;
  variantVersion: TiVoAppVariantVersion;
  provider: TiVoAppProvider;
  onValidationError: (error: string | null) => void;
  onWarnings: (warnings: VariantPromotionWarning[]) => void;
}

const buildAssetsSchema = () => {
  const schemaShape: Record<string, yup.ObjectSchema<{ url: string }>> = {};

  Object.values(IMAGES).forEach((imageType) => {
    schemaShape[imageType] = yup.object().shape({
      url: yup
        .string()
        .url("Must be a valid URL")
        .required(`${imageType} is required`),
    });
  });

  return yup.object().shape(schemaShape);
};

export const validateRS3Requirements = (
  assets: TiVoAssets["prod"]
): string | null => {
  const assetsSchema = buildAssetsSchema();

  try {
    assetsSchema.validateSync(assets, { abortEarly: false });
    return null;
  } catch (err) {
    return "Missing or invalid images";
  }
};

type ValidatePromotionOptions = {
  includePublishingConfirmation?: boolean;
};

export function useVariantPromotion({
  id,
  variantId,
  variantVersion,
  provider,
  onValidationError,
  onWarnings,
}: UseVariantPromotionProps) {
  const devices = useTiVoDeviceData();
  const publications = useVariantPublications(id, variantId);
  const promoteVariant = usePromoteVariant(id, variantId, variantVersion.id);
  const promoteAndPublishVariant = usePromoteAndPublish(
    id,
    variantId,
    variantVersion.id
  );
  const pendingActionRef = useRef<(() => void) | null>(null);

  const validatePromotion = useCallback<
    (
      nextStage: ReleaseStage,
      options?: ValidatePromotionOptions
    ) => {
      error: string | null;
      warnings: VariantPromotionWarning[];
      publishingConfirmationLabels: string | null;
    }
  >(
    (nextStage, options) => {
      const prodAssets = provider?.assets?.prod;
      const targets = variantVersion.targets;
      const imagesError = validateRS3Requirements(prodAssets);
      const targetsConflicts = publications.isSuccess
        ? getPublicationsOutsideTargets(
            devices,
            publications.data.results,
            [nextStage],
            targets
          )
        : null;

      const warnings: VariantPromotionWarning[] = [];

      if (imagesError) {
        warnings.push({ type: "images" });
      }

      if (
        !targetsConflicts ||
        targetsConflicts.clientTypes.length !== 0 ||
        targetsConflicts.oems.length !== 0 ||
        targetsConflicts.platforms.length !== 0
      ) {
        warnings.push({ type: "targets" });
      }

      if (options?.includePublishingConfirmation) {
        const oemIds = devices.getOemsRequiringConfirmation(targets);
        if (oemIds.length) {
          const labels = oemIds
            .map((oemId) => devices.getDeviceLabel(oemId))
            .join(", ");
          warnings.push({
            type: "publishingConfirmation",
            message: `Publishing confirmation required for: ${labels}`,
          });
        }
      }

      if (nextStage === "rs3") {
        return {
          error: imagesError,
          warnings: [],
          publishingConfirmationLabels: null,
        };
      }

      return { error: null, warnings, publishingConfirmationLabels: null };
    },
    [devices, provider, publications, variantVersion]
  );

  const promote = useCallback(
    (nextReleaseStage: ReleaseStage) => {
      onValidationError(null);
      onWarnings([]);

      const validation = validatePromotion(nextReleaseStage);

      if (validation.error) {
        onValidationError(validation.error);
        return;
      }

      if (validation.warnings.length !== 0) {
        pendingActionRef.current = promoteVariant.mutate;
        onWarnings(validation.warnings);
        return;
      }

      promoteVariant.mutate();
    },
    [promoteVariant, validatePromotion, onValidationError, onWarnings]
  );

  const promoteAndPublish = useCallback(
    (nextReleaseStage: ReleaseStage) => {
      // Prevent concurrent mutations
      if (promoteVariant.isPending || promoteAndPublishVariant.isPending) {
        return;
      }

      onValidationError(null);
      onWarnings([]);

      const validation = validatePromotion(nextReleaseStage, {
        includePublishingConfirmation: true,
      });

      if (validation.error) {
        onValidationError(validation.error);
        return;
      }

      if (validation.warnings.length !== 0) {
        pendingActionRef.current = promoteAndPublishVariant.mutate;
        onWarnings(validation.warnings);
        return;
      }

      promoteAndPublishVariant.mutate();
    },
    [
      promoteVariant,
      promoteAndPublishVariant,
      onValidationError,
      onWarnings,
      validatePromotion,
    ]
  );

  const proceedWithPromotion = useCallback(() => {
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  }, []);

  return {
    promote,
    promoteAndPublish,
    proceedWithPromotion,
    isLoading: promoteVariant.isPending || promoteAndPublishVariant.isPending,
    error: promoteVariant.error || promoteAndPublishVariant.error,
  };
}
